import { TypeMessage } from "@/common/enums/general/TypeMessage";
import { IResponseApiGeneric } from "@/interface/general/IResponseApi";
import { HttpGeneric } from "@/models/class/httpGeneric";
import { UserInteractiveService } from "@/Services/user-interactive.service";
import { inject, Injectable } from "@angular/core";
import { map, Observable, tap } from "rxjs";
import { IAnularDocumento, IArchivoAdjunto, IDataMotivo, IDatosAdicionales, IDevolverArchivos, IFirmaCertificada, IOrdenPago, IPaso, IRetornarDocumento, ISaltarPaso, ISaltarPasoAdmin, IVariables } from "./botonera.interface";
import { IPagination } from "@/interface/general/IPagination";
import { Convert } from "@/util/convert";
import { TypeStateSign } from "./botonera.enums";

@Injectable({
  providedIn: 'root',
})
export class BotoneraApiService extends HttpGeneric<string | number> {
  // Url
  private urlPutDocDevolverDocumento = 'FlujoDocRad/FlujoDocDevolverArchivo';
  private urlPostAnularDocumento = 'FlujoDocRad/FlujoDocRadAnular';
  private urlPostEnviarSiguientePaso = 'FlujoDocRad/FlujoDocEnviarSigPaso';
  private urlPostRetornarDocumento = 'FlujoDocRad/FlujoDocRadRetornarDoc';
  private urlPutSaltarPaso = 'FlujoDocRad/FlujoDocSaltarPaso';
  private urlPutSaltarPasoAdmin = 'FlujoDocRad/FlujoDocSaltarPasoxAdmin';
  private urlGetLogMotivos = 'LogMotivos/GetListMotivos';
  private urlPostFirmaCertificada = 'FirmaCertificada/Create';
  private urlPutFirmaCertificada = 'FirmaCertificada/Update';
  private urlPostNotificarRespuesta = 'FlujoDoc/NotificarRespuesta';
  private urlPostGenerarOrdenPago = 'FlujoDocRad/GenerarOrdenPago';

  // Crud Datos Adicionales
  private urlPostDatosAdcionales = 'FlujoDoc/CrearDatosAdicionales';
  private urlPuttDatosAdcionales = 'FlujoDoc/EditarDatosAdicionales';
  private urlDeletetDatosAdcionales = 'FlujoDoc/EliminarDatosAdicionales';

  // servicios
  userInteractive = inject(UserInteractiveService);

  PutDevolverDocumento(data: IDevolverArchivos): Observable<boolean> {
    return this.httpService.Put<IResponseApiGeneric<IDevolverArchivos>>(data, this.urlPutDocDevolverDocumento)
      .pipe(tap((resp) => this._mensaje(resp)), map((resp) => resp.estado));
  }

  PostAnularDocumento(data: IAnularDocumento): Observable<boolean> {
    return this.httpService.Post<IResponseApiGeneric<IAnularDocumento>>(data, this.urlPostAnularDocumento)
      .pipe(tap((resp) => this._mensaje(resp)), map((resp) => resp.estado));
  }

  PutEnviarSiguientePaso(data: IPaso): Observable<boolean> {
    return this.httpService.Put<IResponseApiGeneric<any>>(data, this.urlPostEnviarSiguientePaso)
      .pipe(tap((resp) => this._mensaje(resp)), map((resp) => resp.estado));
  }

  PostRetornarDocumento(data: IRetornarDocumento): Observable<boolean> {
    return this.httpService.Post<IResponseApiGeneric<IRetornarDocumento>>(data, this.urlPostRetornarDocumento)
      .pipe(tap((resp) => this._mensaje(resp)), map((resp) => resp.estado));
  }

  PutSaltarPaso(data: ISaltarPaso): Observable<boolean> {
    return this.httpService.Put<IResponseApiGeneric<ISaltarPaso>>(data, this.urlPutSaltarPaso)
      .pipe(tap((resp) => this._mensaje(resp)), map((resp) => resp.estado));
  }

  PutSaltarPasoAdmin(data: ISaltarPasoAdmin): Observable<boolean> {
  return this.httpService.Put<IResponseApiGeneric<ISaltarPasoAdmin>>(data, this.urlPutSaltarPasoAdmin)
    .pipe(tap((resp) => this._mensaje(resp)), map((resp) => resp.estado));
  }

  GetVerMotivos(IdfdArchivo: number, CodRadicado?: string, pagination?: IPagination) {
    const data = { IdfdArchivo: IdfdArchivo, CodRadicado, Modulo:'flujodoc', ExportExcel: false, ByteFile: false };
    const params = Convert.ToParams(data);
    return this.httpService.Get<IResponseApiGeneric<IDataMotivo[]>>(this.urlGetLogMotivos, false, { params }, pagination)
      .pipe(tap((resp) => this._mensaje(resp)), map((resp) => resp.data));
  }

  getFile(url: string) {
    return this.httpService.Get<IResponseApiGeneric<IArchivoAdjunto>>(url, true)
      .pipe(tap((resp) => this._mensaje(resp)),map((resp) => resp.data));
  }

  PostFirmaCertificada(data: IFirmaCertificada): Observable<boolean> {
    return this.httpService.Post<IResponseApiGeneric<void>>(data, this.urlPostFirmaCertificada)
      .pipe(tap((resp) => this._mensaje(resp)), map((resp) => resp.estado));
  }

  PutFirmaCertificada(data: IFirmaCertificada): Observable<boolean> {
    return this.httpService.Put<IResponseApiGeneric<IFirmaCertificada>>(data, this.urlPutFirmaCertificada)
      .pipe(
        tap((resp) => this._mensaje(resp, resp.data.Estado === TypeStateSign.SIGNED ? TypeMessage.SUCCESS : TypeMessage.WARNING)), 
        map((resp) => resp.estado));
  }

  PostNotificarRespuesta(data: IVariables): Observable<boolean> {
    return this.httpService.Post<IResponseApiGeneric<boolean>>(data, this.urlPostNotificarRespuesta)
      .pipe(
        tap((resp) => this._mensaje(resp, resp.estado === true ? TypeMessage.SUCCESS : TypeMessage.WARNING)), 
        map((resp) => resp.estado));
  }

  PostGenerarOrdenPago(data: IVariables): Observable<boolean> {
    return this.httpService.Post<IResponseApiGeneric<IOrdenPago>>(data, this.urlPostGenerarOrdenPago)
      .pipe(
        tap((resp) => this._mensaje(resp, resp.estado === true ? TypeMessage.SUCCESS : TypeMessage.WARNING)), 
        map((resp) => resp.estado));
  }

  PostDatosAdicionales(data: IDatosAdicionales): Observable<boolean> {
    return this.httpService.Post<IResponseApiGeneric<IDatosAdicionales>>(data, this.urlPostDatosAdcionales)
      .pipe(tap((resp) => this._mensaje(resp)), map((resp) => resp.estado));
  }

  PutDatosAdcionales(data: IDatosAdicionales): Observable<boolean> {
    return this.httpService.Put<IResponseApiGeneric<IDatosAdicionales>>(data, this.urlPuttDatosAdcionales)
      .pipe(tap((resp) => this._mensaje(resp)), map((resp) => resp.estado));
  }

  DeletetDatosAdcionales(data: IDatosAdicionales): Observable<boolean> {
    return this.httpService.Delete<IResponseApiGeneric<IDatosAdicionales>>(this.urlDeletetDatosAdcionales, data)
      .pipe(tap((resp) => this._mensaje(resp)), map((resp) => resp.estado));
  }

  private _mensaje(response: IResponseApiGeneric<unknown>, typeMessage = TypeMessage.SUCCESS) {
    return this.userInteractive.sendMessage(typeMessage, response.mensaje);
  }
}